import React, { useRef } from "react"
import { Search, X } from "lucide-react"
import { Input } from "@aix/ui"
import { useHotkeys } from "react-hotkeys-hook"

interface SearchBarProps {
  value: string
  onChange: (value: string) => void
  matchCount?: number
  totalCount?: number
}

const SearchBar: React.FC<SearchBarProps> = ({ value, onChange, matchCount, totalCount }) => {
  const inputRef = useRef<HTMLInputElement>(null)

  // Keyboard shortcut: Cmd+F / Ctrl+F
  useHotkeys(
    "mod+f",
    (e) => {
      e.preventDefault()
      inputRef.current?.focus()
    },
    { enableOnFormTags: false },
  )

  // Escape to clear
  useHotkeys(
    "escape",
    () => {
      if (value) {
        onChange("")
        inputRef.current?.blur()
      }
    },
    { enableOnFormTags: true },
  )

  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 w-96">
      <div className="bg-background border border-border rounded-lg shadow-lg px-3 py-2 flex items-center gap-2">
        <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search tables..."
          className="border-0 bg-transparent focus:ring-0 flex-1 text-sm p-0"
        />
        {value && (
          <>
            {matchCount !== undefined && totalCount !== undefined && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {matchCount} of {totalCount}
              </span>
            )}
            <button
              onClick={() => onChange("")}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Clear search (Esc)"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        )}
        <span className="text-xs text-muted-foreground">⌘F</span>
      </div>
    </div>
  )
}

export default SearchBar
